import React, {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import {useThemeProvider} from "../../../../../mosaic/utils/ThemeContext.jsx";
import {chartColors, getHtmlLegendPlugin} from "../../../../../mosaic/charts/ChartjsConfig.jsx";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {Chart} from "chart.js";
import moment from "moment-timezone";
import {useLegacyPeriodReportError} from "../../Layout/LegacyPeriodReportBoundary.jsx";
import toast from "react-hot-toast";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import Button from "../../../../../components/Button.jsx";
import CampaignOrderBasis from "./CampaignOrderBasis.jsx";
import {campaignChartColors, campaignChartDisplayLabels, campaignChartIdentity, campaignForFilterRegistry, campaignGraphForSnapshot, campaignMatchesFilter, campaignOrderSeries} from "./campaignChartPresentation.js";

export default function OrdersInTimeGraph(
    {
        project,
        fullWidth = false,
        showLegend = false,
        showHideButton = false,
        hidden = false,
        setHidden = () => {
        },
        data: providedData = undefined,
        snapshot: providedSnapshot = undefined,
        showOrderBasis = false,
        metricContext = undefined,
    }
) {
    let {t} = useTranslation();
    let {et} = useLegacyPeriodReportError();

    const canvas = useRef(null);
    const legend = useRef(null);

    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';

    const {gridColor, textColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor} = chartColors;

    let [userSettings] = useUserSettings();
    const isMarketingContext = metricContext === undefined ? Boolean(userSettings?.isMarketing) : metricContext === "marketing";

    let {
        since,
        until,
        sourcesRemoved,
        campaignSearchFilter,
        campaignsAllowListed,
        campaignsPossible,
        centresAllowListed,
        domainsAllowListed,
        sourceRegister,
        campaignRegister,
    } = useContext(FilterContext);
    const {customerWholesaleRequestFilter} = useContext(FilterContext);

    let [loadedData, setLoadedData] = useState(null);
    let responseData = providedData === undefined ? loadedData : providedData;
    let snapshot = providedSnapshot ?? responseData?.snapshot;
    let data = campaignGraphForSnapshot(responseData, snapshot);

    let authorityConfiguration = useAuthorityConfiguration();

    useEffect(
        () => {
            if (providedData !== undefined) {
                return;
            }

            let active = true;
            setLoadedData(null);
            new ErpCalculationApi(authorityConfiguration).ordersInTime({
                projectId: project.id,
                erpFilter: {
                    ...customerWholesaleRequestFilter,
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedDomainIds: domainsAllowListed,
                    selectedCentreIds: centresAllowListed,
                },
            }).then((nextData) => {
                if (active) {
                    setLoadedData(nextData);
                }
            }).catch(e => {
                if (active) {
                    et(e).then((message) => { if (message) toast.error(message); });
                }
            });

            return () => {
                active = false;
            };
        },
        [
            providedData,
            project.id,
            since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'),
            centresAllowListed,
            domainsAllowListed,
            customerWholesaleRequestFilter,
        ]
    );

    useEffect(() => {
        const ctx = canvas.current;
        const campaigns = Object.values(data?.dataSources ?? []);
        const campaignLabels = campaignChartDisplayLabels(campaigns);

        //  Chart.register(LogarithmicScale);

        const pluginsArray = [];

        if (showLegend) {
            pluginsArray.push(getHtmlLegendPlugin(legend));
        }

        // noinspection JSUnresolvedReference
        const newChart = new Chart(ctx, {
            type: 'line',
            data: {
                datasets: campaigns.map((campaign) => {
                    sourceRegister(campaign.integrationName);
                    campaignRegister(campaignForFilterRegistry(campaign));

                    if (sourcesRemoved.includes(campaign.integrationName)) {
                        return {};
                    }

                    // noinspection JSUnresolvedReference
                    if (
                        campaignSearchFilter.length > 0 &&
                        !campaign.campaignName
                            .toLowerCase()
                            .includes(campaignSearchFilter.toLowerCase())
                    ) {
                        return {};
                    }

                    if (
                        campaignsAllowListed.length > 0 &&
                        !campaignMatchesFilter(campaign, campaignsAllowListed, Object.values(campaignsPossible))
                    ) {
                        return {};
                    }

                    const label = campaignLabels.get(campaignChartIdentity(campaign)) ?? campaign.campaignName;
                    const colors = campaignChartColors(campaign);

                    return {
                        label,
                        data: campaignOrderSeries(campaign, isMarketingContext),
                        fill: false,
                        backgroundColor: colors.backgroundColor,
                        borderColor: colors.borderColor,
                        borderWidth: 2,
                        pointBorderWidth: 1,
                    };
                }),
            },
            options: {
                animations: null,
                responsive: true,
                scales: {
                    y: {
                        border: {
                            display: false,
                        },
                        ticks: {
                            maxTicksLimit: 7,
                            color: darkMode ? textColor.dark : textColor.light,
                        },
                        grid: {
                            color: darkMode ? gridColor.dark : gridColor.light,
                        },
                    },
                    x: {
                        border: {
                            display: false,
                        },
                        ticks: {
                            padding: 10,
                            align: 'center',
                            color: darkMode ? textColor.dark : textColor.light,
                            callback: function (val, index) {
                                return moment(data?.labels[index] ?? '').format('DD.MM.');
                            },
                        },
                        grid: {
                            display: false,
                        },
                    },
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => false, // Disable tooltip title
                            label: (context) => {
                                return [
                                    moment(context.label).format('DD.MM.'),
                                    context.dataset.label,
                                    t('orders.x-orders', {
                                        count: Object.values(context.dataset.data)[
                                            context.dataIndex
                                            ],
                                    }),
                                ];
                            },
                        },
                        bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
                        backgroundColor: darkMode ? tooltipBgColor.dark : tooltipBgColor.light,
                        borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
                        opacity: 0.8,
                    },
                    legend: { display: false, },
                    ...(showLegend && {
                        htmlLegend: {
                            containerID: 'lgnd-orders-intime',
                        },
                    }),
                },
                interaction: {
                    intersect: false,
                    mode: 'nearest',
                },
                maintainAspectRatio: false,
                elements: {
                    point: {
                        radius: 2,
                        hoverRadius: 3,
                    },
                },
            },
            plugins: pluginsArray,
        });

        return () => {
            newChart.destroy();
        };
    }, [data, userSettings, sourcesRemoved, campaignsAllowListed, campaignsPossible, campaignSearchFilter, showLegend]);

    return (<LoadingComponent
        loading={data === null}
        className={
            'comp__roundedBox reporting-surface rounded-lg bg-white dark:bg-slate-800' + ' ' +
            'border-slate-200 dark:border-slate-700' + ' ' +
            'text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300' + ' ' +
            'shadow-xl rounded-lg border-1 ' +
            (fullWidth ? 'col-span-12' : 'col-span-12 md:col-span-6') + ' ' +
            (!showLegend ? 'pb-2' : '')
        }>
        <div
            className={'flex justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
            <div className="flex items-center gap-3">
                <h2 className={'text-lg font-semibold text-gray-800 dark:text-gray-100'}>
                    {t('project.orders-in-time')}
                </h2>
                {showOrderBasis && <CampaignOrderBasis isMarketing={isMarketingContext}/>}
            </div>

            {showHideButton &&
                <Button
                    variant={'outline-violet'}
                    onClick={() => setHidden(!hidden)}
                >
                    {hidden ? t('show') : t('hide')}
                </Button>
            }
        </div>
        <div className={'pl-4 pt-3 ' + (hidden ? 'hidden' : '')}>
            <div>
                <canvas ref={canvas} height={showLegend ? 450 : 250}></canvas>
            </div>
            <div ref={legend} className="legend-wrap"></div>
        </div>
    </LoadingComponent>);
}
